import { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useParams, Navigate } from "react-router-dom";
import { Container } from "../components/Container";
import { useIsAdmin } from "../hooks/useIsAdmin";
import { Card } from "../components/Card";
import { Button } from "../components/Button";
import { LoadingContainer } from "../components/LoadingContainer";
import { EmptyState } from "../components/EmptyState";
import { Title } from "../components/Title";
import {
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
} from "../components/Table";
import { useGetResellerInvoicesQuery, useGenerateResellerInvoiceMutation } from "../query/invoicesQuery";
import { ResellerInvoiceResultPanel } from "./components/ResellerInvoiceResultPanel";
import { formatDate, formatPeriod } from "../utils/dateUtils";
import { spacing } from "@styling/sizing";
import type { RestResellerInvoiceData } from "@KassaroAB/shared/types/invoices/resellerInvoice";
import { formatMoney } from "../utils/formatMoney";
import type { ResellerId } from "@KassaroAB/shared/types/ids";

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${spacing.x4}px;
`;

export const ResellerInvoicingView = () => {
  const { t } = useTranslation();
  const { resellerId } = useParams<{ resellerId: ResellerId }>();
  const isAdmin = useIsAdmin();
  const { data: invoices, isLoading } = useGetResellerInvoicesQuery(resellerId);
  const { mutate: generateInvoice, isPending: isGenerating } =
    useGenerateResellerInvoiceMutation(resellerId);
  const [selectedInvoice, setSelectedInvoice] = useState<RestResellerInvoiceData | null>(null);
  const [generatedInvoice, setGeneratedInvoice] = useState<RestResellerInvoiceData | null>(null);

  if (!isAdmin) {
    return <Navigate to={`/resellers/${resellerId}/overview`} replace />;
  }

  const handleGenerate = () => {
    generateInvoice(undefined, {
      onSuccess: (data) => setGeneratedInvoice(data),
      onError: () => {},
    });
  };

  if (isLoading) {
    return (
      <Container>
        <PageHeader>
          <Title>{t("invoice.sectionTitle")}</Title>
        </PageHeader>
        <LoadingContainer message={t("invoice.loading")} />
      </Container>
    );
  }

  return (
    <Container>
      <PageHeader>
        <Title>{t("invoice.sectionTitle")}</Title>
        <Button
          onClick={handleGenerate}
          disabled={isGenerating}
        >
          {isGenerating ? t("invoice.generating") : t("invoice.generateButton")}
        </Button>
      </PageHeader>

      {!invoices || invoices.length === 0 ? (
        <Card>
          <EmptyState message={t("invoice.emptyList")} />
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <tr>
                <TableHeaderCell>{t("invoice.period")}</TableHeaderCell>
                <TableHeaderCell>{t("invoice.generatedAt")}</TableHeaderCell>
                <TableHeaderCell>{t("invoice.total")}</TableHeaderCell>
                <TableHeaderCell>{t("invoice.sent")}</TableHeaderCell>
              </tr>
            </TableHeader>
            <TableBody>
              {[...invoices]
                .sort((a, b) => new Date(b.generatedAt).getTime() - new Date(a.generatedAt).getTime())
                .map((invoice) => (
                  <TableRow
                    key={invoice.period}
                    clickable
                    onClick={() => setSelectedInvoice(invoice)}
                  >
                    <TableCell noWrap>{formatPeriod(invoice.period)}</TableCell>
                    <TableCell noWrap>{formatDate(invoice.generatedAt)}</TableCell>
                    <TableCell noWrap>{formatMoney(invoice.totalAmount)}</TableCell>
                    <TableCell noWrap>{invoice.sent ?? false ? t("common.yes") : t("common.no")}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {selectedInvoice && (
        <ResellerInvoiceResultPanel
          invoice={selectedInvoice}
          resellerId={resellerId}
          onClose={() => setSelectedInvoice(null)}
          onInvoiceUpdated={setSelectedInvoice}
        />
      )}
      {generatedInvoice && (
        <ResellerInvoiceResultPanel
          invoice={generatedInvoice}
          resellerId={resellerId}
          onClose={() => setGeneratedInvoice(null)}
          onInvoiceUpdated={setGeneratedInvoice}
        />
      )}
    </Container>
  );
};
